import { useEffect } from 'react';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { px } from '@/lib/utils';

const MOBILE_VIEWPORT_CONTENT = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
const DESKTOP_VIEWPORT_CONTENT = 'width=device-width, initial-scale=1.0, viewport-fit=cover';

const useVisualViewport = () => {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.getElementById('root');
    if (!root) return;

    const isTextInputFocused = () => {
      const el = document.activeElement;
      return el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement;
    };

    const update = () => {
      const keyboardViewportActive =
        isTextInputFocused() &&
        (vv.offsetTop > 1 || vv.height < window.innerHeight - 1);

      if (keyboardViewportActive) {
        root.style.height = px(vv.height);
        root.style.transform = vv.offsetTop > 1 ? `translateY(${px(vv.offsetTop)})` : '';
      } else {
        root.style.height = '';
        root.style.transform = '';
      }
    };

    const onFocusOut = () => requestAnimationFrame(update);

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);
};

export const usePageSetup = () => {
  useVisualViewport();
  const isMobileDevice = useIsMobileDevice();

  useEffect(() => {
    document.documentElement.setAttribute('data-device', isMobileDevice ? 'mobile' : 'desktop');
    return () => document.documentElement.removeAttribute('data-device');
  }, [isMobileDevice]);

  useEffect(() => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) return;
    viewportMeta.setAttribute('content', isMobileDevice ? MOBILE_VIEWPORT_CONTENT : DESKTOP_VIEWPORT_CONTENT);
  }, [isMobileDevice]);

  useEffect(() => {
    if (!isMobileDevice) return;

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };
    const preventPinchTouch = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    let touchStartX = 0;
    let touchStartY = 0;
    const trackTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        touchStartX = event.touches[0]!.clientX;
        touchStartY = event.touches[0]!.clientY;
      }
    };
    const preventHorizontalPan = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const dx = Math.abs(event.touches[0]!.clientX - touchStartX);
      const dy = Math.abs(event.touches[0]!.clientY - touchStartY);
      if (dx > dy && dx > 5) {
        event.preventDefault();
      }
    };

    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });
    document.addEventListener('gestureend', preventGesture, { passive: false });
    document.addEventListener('touchstart', preventPinchTouch, { passive: false });
    document.addEventListener('touchstart', trackTouchStart, { passive: true });
    document.addEventListener('touchmove', preventPinchTouch, { passive: false });
    document.addEventListener('touchmove', preventHorizontalPan, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
      document.removeEventListener('touchstart', preventPinchTouch);
      document.removeEventListener('touchstart', trackTouchStart);
      document.removeEventListener('touchmove', preventPinchTouch);
      document.removeEventListener('touchmove', preventHorizontalPan);
    };
  }, [isMobileDevice]);

  useEffect(() => {
    const handleExternalClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      let destination: URL;
      try {
        destination = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (destination.protocol !== 'http:' && destination.protocol !== 'https:') return;
      if (destination.origin === window.location.origin) return;
 
      event.preventDefault();
      event.stopPropagation();
      window.open(destination.href, '_blank', 'noopener,noreferrer');
    };

    document.addEventListener('click', handleExternalClick, true);
    return () => document.removeEventListener('click', handleExternalClick, true);
  }, []);

  return isMobileDevice;
};
